/**
 * BlockWirePreview — the live wire thumbnail (Faz 5.8, OCS BlockPreviewCanvas
 * parity): the definition's entities resolve through entityToPolyline into
 * an SVG polylines drawing, bbox-fit with padding. Results are memoized per
 * definition reference, and tiles render lazily (only the visible tab's
 * list mounts previews). Empty definitions show the dark placeholder box.
 */

import { useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import type { BlockDef } from '../../state/commandAuxStores';
import { entityToPolyline } from '../../core/commands/runners/lib';

export interface BlockWirePreviewProps {
    def: BlockDef;
    /** Square tile size in px. */
    size?: number;
}

interface Wire {
    points: [number, number][];
}

/** Tessellate the definition once per def reference (entities array identity). */
function useWires(def: BlockDef): Wire[] {
    return useMemo(() => {
        const wires: Wire[] = [];
        for (const e of def.entities) {
            const poly = entityToPolyline(e);
            if (!poly || poly.length < 2) continue;
            wires.push({ points: poly.map((p) => [p[0], p[1]] as [number, number]) });
        }
        return wires;
    }, [def, def.entities]);
}

export function BlockWirePreview({ def, size = 64 }: BlockWirePreviewProps) {
    const wires = useWires(def);

    // Bbox fit + 6% padding (OCS BlockPreviewCanvas framing).
    const view = useMemo(() => {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const w of wires) {
            for (const [x, y] of w.points) {
                if (x < minX) minX = x;
                if (y < minY) minY = y;
                if (x > maxX) maxX = x;
                if (y > maxY) maxY = y;
            }
        }
        if (!Number.isFinite(minX)) return null;
        const padX = Math.max((maxX - minX) * 0.06, 0.01);
        const padY = Math.max((maxY - minY) * 0.06, 0.01);
        return { minX: minX - padX, minY: minY - padY, w: (maxX - minX) + padX * 2, h: (maxY - minY) + padY * 2 };
    }, [wires]);

    if (!view) {
        return (
            <Box
                w={`${size}px`}
                h={`${size}px`}
                borderRadius="4px"
                bg="surface.100"
                border="1px solid"
                borderColor="border.base"
                flexShrink={0}
            />
        );
    }

    return (
        <Box
            as="svg"
            w={`${size}px`}
            h={`${size}px`}
            viewBox={`${view.minX} ${view.minY} ${view.w} ${view.h}`}
            preserveAspectRatio="xMidYMid meet"
            borderRadius="4px"
            bg="surface.100"
            border="1px solid"
            borderColor="border.base"
            flexShrink={0}
            sx={{ '& polyline': { fill: 'none', stroke: 'var(--chakra-colors-text-primary)', strokeWidth: 2, vectorEffect: 'non-scaling-stroke', strokeLinejoin: 'round', strokeLinecap: 'round' } }}
        >
            {wires.map((w, i) => (
                <polyline key={i} points={w.points.map(([x, y]) => `${x},${y}`).join(' ')} />
            ))}
        </Box>
    );
}
